import { designDocumentSchema } from '@repo/api-contracts';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { trpc } from '@/shared/api';
import { clearDraft, saveDraft } from './draft-storage';
import { useEditorStore } from './editor-store';
import { errorNodeTargets } from './error-nodes';
import { describeSaveError, type SaveErrorInfo } from './save-error';
import { validateForSave } from './save-validation';

export type SaveOutcome =
  | { readonly status: 'saved'; readonly designId: string }
  | { readonly status: 'login-required' }
  | { readonly status: 'failed'; readonly error: SaveErrorInfo };

interface SaveDesignInput {
  readonly productId: string;
}

export interface SaveDesign {
  readonly save: () => Promise<SaveOutcome>;
  /**
   * 저장 없이 계약 검사만 한다(비회원 [견적 요청하기] 전, G5). 잘못된 레이어가 있으면 저장과 같은 invalid 오류를
   * lastError 에 남기고(첫 레이어 선택 포함) false, 올바르면 남은 오류를 지우고 true.
   */
  readonly validate: () => boolean;
  readonly isSaving: boolean;
  readonly lastError: SaveErrorInfo | null;
  /** lastError 가 가리키는 맨 위 레이어 id(레이어 목록 강조용). */
  readonly invalidNodeIds: ReadonlySet<string>;
}

interface ErrorLike {
  readonly message: string;
  readonly data?: { readonly code?: string } | null;
}

const isErrorLike = (error: unknown): error is ErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof error.message === 'string';

const toErrorLike = (error: unknown): ErrorLike => (isErrorLike(error) ? error : { message: '' });

/**
 * 디자인 저장(U2·§6-5). 문서만 보낸다 — 계약 strict 파싱으로 px·워프·이미지 키가 없음을 보장한다.
 * 비로그인은 임시 보관 뒤 로그인으로 보내고(돌아올 경로는 RequireAuth 와 같은 state.from), 성공하면 URL 에 designId 를 적는다.
 */
export const useSaveDesign = ({ productId }: SaveDesignInput): SaveDesign => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();
  const createMutation = trpc.designs.create.useMutation();
  const updateMutation = trpc.designs.update.useMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<SaveErrorInfo | null>(null);
  const liveDocument = useEditorStore((state) => state.document);
  const invalidNodeIds = useMemo(
    () => new Set(errorNodeTargets(liveDocument, lastError).map((target) => target.nodeId)),
    [liveDocument, lastError],
  );

  // 오류가 새로 오면 가리킨 첫 레이어를 그 컴포넌트에서 선택한다(평면 편집으로 돌아온다).
  useEffect(() => {
    const [first] = errorNodeTargets(useEditorStore.getState().document, lastError);
    if (first !== undefined) useEditorStore.getState().previewHit(first.nodeId, first.componentId);
  }, [lastError]);

  const save = async (): Promise<SaveOutcome> => {
    const { document, designId, markSaved } = useEditorStore.getState();
    if (!isAuthenticated) {
      saveDraft(productId, document);
      navigate('/sign-in', { state: { from: `${location.pathname}${location.search}` } });
      return { status: 'login-required' };
    }
    const invalid = validateForSave(document);
    if (invalid !== null) {
      setLastError(invalid);
      return { status: 'failed', error: invalid };
    }
    setIsSaving(true);
    setLastError(null);
    try {
      // 위에서 검사했으므로 여기서는 strict 파싱으로 본문을 계약 모양으로만 줄인다(px·워프 키 없음 보장).
      const parsed = designDocumentSchema.parse(document);
      const result =
        designId === null
          ? await createMutation.mutateAsync({ document: parsed })
          : await updateMutation.mutateAsync({ id: designId, document: parsed });
      markSaved(result.id);
      clearDraft(productId);
      await utils.designs.get.invalidate({ id: result.id });
      if (designId === null) navigate({ search: `?designId=${result.id}` }, { replace: true });
      return { status: 'saved', designId: result.id };
    } catch (error) {
      const info = describeSaveError(toErrorLike(error));
      setLastError(info);
      return { status: 'failed', error: info };
    } finally {
      setIsSaving(false);
    }
  };

  const validate = (): boolean => {
    const invalid = validateForSave(useEditorStore.getState().document);
    setLastError(invalid);
    return invalid === null;
  };

  return { save, validate, isSaving, lastError, invalidNodeIds };
};
